import {
  BiometricPermissionState,
  BiometricSecurityStrength,
  Device,
  DeviceErrorCodes,
  DeviceSecurityType,
  SupportedBiometricType,
  VaultType,
  type IdentityVaultConfig,
  type VaultError,
} from '@ionic-enterprise/identity-vault';
import { createVault, defaultPrimericaVaultKey } from './vaultFactory';
import { UnlockMode } from './definitions';
import { CapacitorFacade } from '../../capacitorPlugin';
import DynatracePlugin from '@/plugins/dynatracePlugin';

const sessionKey = 'session';
const unlockModePreferenceKey = 'LastUnlockMode';
const isNative = CapacitorFacade.isNativePlatform();
const defaultVaultConfig: IdentityVaultConfig = {
  key: 'com.primerica.myprimerica',
  type: VaultType.SecureStorage,
  deviceSecurityType: DeviceSecurityType.None,
  lockAfterBackgrounded: 240000, // 300000,
  unlockVaultOnLoad: true,
};
let vault;

// Create the vault;
try {
  vault = createVault();
} catch (error) {
  throw new Error('CANNOT CREATE VAULT', { cause: error });
}

// see https://ionic.io/docs/identity-vault/classes/device#sethidescreenonbackground
// Device.setHideScreenOnBackground(true);

const _provision = async () => {
  try {
    // Check biometric permissions states on iOS (always granted on Android and on iOS with TouchID).
    // User has not yet been prompted to allow FaceID, so show biometrics prompts
    if ((await Device.isBiometricsAllowed()) === BiometricPermissionState.Prompt) {
      await Device.showBiometricPrompt({
        iosBiometricsLocalizedReason: 'Authenticate to continue',
        androidBiometricsPromptSubtitle: '',
        androidBiometricsPromptDescription: '',
      });
    }
  } catch (e: unknown) {
    if (e && typeof e === 'object' && 'code' in e) {
      switch (e.code) {
        case DeviceErrorCodes.Unknown:
          DynatracePlugin.reportCustomError('PC2_MOBILE_ERR', 'Unknown error has occurred!');
          break;
        case DeviceErrorCodes.InvalidArguments:
          DynatracePlugin.reportCustomError('PC2_MOBILE_ERR', 'Arguments were invalid!');
          break;
        case DeviceErrorCodes.UserCanceledInteraction:
          DynatracePlugin.reportCustomError(
            'PC2_MOBILE_ERR',
            'You cancelled the authentication dialog!'
          );
          break;
        case DeviceErrorCodes.SecurityNotAvailable:
          DynatracePlugin.reportCustomError(
            'PC2_MOBILE_ERR',
            'You need to set up system level passcode!'
          );
          break;
        case DeviceErrorCodes.AuthFailed:
          DynatracePlugin.reportCustomError('PC2_MOBILE_ERR', 'User Authentication failed!');
          break;
        case DeviceErrorCodes.BiometricsLockedOut:
          DynatracePlugin.reportCustomError(
            'PC2_MOBILE_ERR',
            'Biometrics has been locked out, too many failed attempts!'
          );
          break;
        case DeviceErrorCodes.BiometricsNotEnabled:
          DynatracePlugin.reportCustomError(
            'PC2_MOBILE_ERR',
            'Biometrics authentication is not enabled or supported on this device!'
          );
          break;
        default:
          DynatracePlugin.reportCustomError(
            'PC2_MOBILE_ERR',
            'Biometrics authentication is not enabled or supported on this device!'
          );
          throw e;
      }
    }
    throw e;
  }
};

export const initializeSessionVault = async () => {
  try {
    await vault.initialize(defaultVaultConfig);
  } catch (e) {
    DynatracePlugin.reportCustomError('PC2_MOBILE_ERR', 'Error on initializeSessionVault');
    await vault.clear();
    throw e; // rethrow the error
  }
};

export const setVaultErrorHandler = (callback: (e: VaultError) => void | Promise<void>) => {
  vault.onError(async (err: VaultError) => {
    DynatracePlugin.reportCustomError('PC2_MOBILE_ERR', `VaultError ${JSON.stringify(err)}`);
    await callback(err);
  });
};

export const getVaultConfig = async () => {
  const exists = !(await vault.isEmpty());
  if (exists) {
    return vault.config;
  }
  return null;
};

export const getVaultType = async () => {
  const exists = !(await vault.isEmpty());
  if (exists) {
    return vault.config?.type;
  }
  return null;
};

export const isVaultEmpty = async () => !(await vault.isEmpty());

export const canUseLocking = () => isNative;

// TODO: review usage because logic is wrong
// TODO: delete this
export const canUnlock = async () => {
  // ? consider Promise.all
  const exists = !(await vault.isEmpty());
  const isLocked = !(await vault.isLocked());
  return isNative && exists && isLocked;
};

// TODO: add unit test
export const isVaultLocked = async () => {
  // ? consider Promise.all
  const exists = !(await vault.isEmpty());
  const isLocked = await vault.isLocked();
  return isNative && exists && isLocked;
};

export const lockVault = async () => vault.lock();

export const unlockVault = async () => vault.unlock();

/*
 * SESSION RELATED
 */

export const clearSession = async () => vault.clear();

export const getSession = async () => vault.getValue(sessionKey);

// eslint-disable-next-line
export const setSession = async (authResult: any) => vault.setValue(sessionKey, authResult);

/*
 * DEVICE RELATED
 */

export const canUseBiometrics = async () => isNative && Device.isBiometricsEnabled();

export const getAvailableHardware = async () => Device.getAvailableHardware();

export const getBiometricStrengthLevel = async () => Device.getBiometricStrengthLevel();

export const hasStrongBiometricsStrengthLevel = async () =>
  (await Device.getBiometricStrengthLevel()) === BiometricSecurityStrength.Strong;

export const hasFaceId = async () =>
  (await Device.getAvailableHardware()).filter(
    (biometricType) => biometricType === SupportedBiometricType.Face
  ).length > 0;

export const hasTouchId = async () =>
  (await Device.getAvailableHardware()).filter(
    (biometricType) => biometricType === SupportedBiometricType.Fingerprint
  ).length > 0;

/*
 * PREFERENCE RELATED AND UNLOCK MODE RELATED
 */

// TODO: add unit test
// see https://ionic.io/docs/identity-vault/vue/getting-started#why-the-unlockmode
export const getUnlockMode = async () => {
  const vaultConfig = !(await vault.isEmpty()) ? vault.config : null;
  if (!vaultConfig) throw new Error('Vault is empty');
  const { type, deviceSecurityType } = vaultConfig;
  if (type === VaultType.DeviceSecurity && deviceSecurityType === DeviceSecurityType.Biometrics) {
    return UnlockMode.biometrics;
  } else if (type === VaultType.DeviceSecurity && deviceSecurityType === DeviceSecurityType.Both) {
    return UnlockMode.biometricsWithPasscode;
  } else if (
    type === VaultType.DeviceSecurity &&
    deviceSecurityType === DeviceSecurityType.SystemPasscode
  ) {
    return UnlockMode.systemPasscode;
  } else if (type === VaultType.SecureStorage && deviceSecurityType === DeviceSecurityType.None) {
    return UnlockMode.secureStorage;
  }
};

// see https://ionic.io/docs/identity-vault/vue/getting-started#why-the-unlockmode
export const setUnlockMode = async (unlockMode: UnlockMode) => {
  let type;
  let deviceSecurityType;

  switch (unlockMode) {
    case UnlockMode.biometrics:
      await _provision();
      type = VaultType.DeviceSecurity;
      deviceSecurityType = DeviceSecurityType.Biometrics;
      break;

    case UnlockMode.biometricsWithPasscode:
      await _provision();
      type = VaultType.DeviceSecurity;
      deviceSecurityType = DeviceSecurityType.Both;
      break;

    case UnlockMode.systemPasscode:
      type = VaultType.DeviceSecurity;
      deviceSecurityType = DeviceSecurityType.SystemPasscode;
      break;

    case UnlockMode.secureStorage:
      type = VaultType.SecureStorage;
      deviceSecurityType = DeviceSecurityType.None;
      break;

    default:
      type = VaultType.SecureStorage;
      deviceSecurityType = DeviceSecurityType.None;
  }

  await vault.updateConfig({
    key: defaultPrimericaVaultKey,
    ...vault.config,
    type,
    deviceSecurityType,
  });

  await CapacitorFacade.setPreference({ key: unlockModePreferenceKey, value: unlockMode });
};

// see https://ionic.io/docs/identity-vault/vue/getting-started#why-the-unlockmode
export const setUnlockModeToBiometrics = () => setUnlockMode(UnlockMode.biometrics);

// see https://ionic.io/docs/identity-vault/vue/getting-started#why-the-unlockmode
export const setUnlockModeToSecureStorage = () => setUnlockMode(UnlockMode.secureStorage); // TODO: add unit test

export const getUnlockModePreference = async () =>
  CapacitorFacade.getPreference(unlockModePreferenceKey); // TODO: add unit test

export const isUnlockModePreferenceSet = async () =>
  !!(await CapacitorFacade.getPreference(unlockModePreferenceKey));

export const isUnlockModePreferenceSetToBiometrics = async () =>
  (await CapacitorFacade.getPreference(unlockModePreferenceKey)) === UnlockMode.biometrics; // TODO: add unit test

/*
 * BUSINESS RELATED
 */

// TODO: add unit test
export const unlockVaultWithBiometrics = async () => {
  const _getUnlockMode = await getUnlockMode();
  if (
    _getUnlockMode === UnlockMode.biometrics ||
    _getUnlockMode === UnlockMode.biometricsWithPasscode
  ) {
    return _provision().then(() => vault.unlock());
  } else {
    return null;
  }
};

/*
This is an adapter service to encapsulate operations on the vault and
expose an API for managing user session
 */
export default {
  // * VAULT RELATED
  initializeSessionVault,
  setVaultErrorHandler,
  getVaultConfig,
  getVaultType,

  isVaultEmpty,
  canUseLocking,
  canUnlock,
  isVaultLocked,

  lockVault,
  unlockVault,

  // * SESSION RELATED
  clearSession, // ?
  getSession, // ?
  setSession, // ?

  // * DEVICE RELATED
  canUseBiometrics,
  getAvailableHardware,
  getBiometricStrengthLevel,
  hasStrongBiometricsStrengthLevel,
  hasFaceId,
  hasTouchId,

  // * PREFERENCE RELATED AND UNLOCK MODE RELATED
  // see https://ionic.io/docs/identity-vault/vue/getting-started#why-the-unlockmode
  getUnlockMode,
  setUnlockMode,
  setUnlockModeToBiometrics,
  setUnlockModeToSecureStorage,
  getUnlockModePreference,
  isUnlockModePreferenceSet,
  isUnlockModePreferenceSetToBiometrics,

  // * BUSINESS RELATED
  unlockVaultWithBiometrics,
};
